import { useMemo, useState } from 'react';
import { isBefore } from 'shared/util/datetime-fns';
import {
    birthDateTooOld,
    invalidBirthDate,
    invalidMessageField,
    invalidNameField,
    invalidPhone,
    isValidDate,
    tooYoung,
} from '@/lib/order/validation';
import { normalizeQueryParam } from '@/scenes/createRentalRequestPage/utils/normalizeQueryParam';

type ValidationProps = {
    firstname: string;
    surname: string;
    dateOfBirth: string;
    landcode: string;
    phone: string;
    message: string;
    startDate: string | string[] | undefined;
    endDate: string | string[] | undefined;
    guests: string | string[] | undefined;
};

export const useRentalRequestValidation = ({
    firstname,
    surname,
    dateOfBirth,
    landcode,
    phone,
    message,
    startDate,
    endDate,
    guests,
}: ValidationProps) => {
    const [shouldValidate, setShouldValidate] = useState(false);

    const startDateNormalized = useMemo(() => normalizeQueryParam(startDate), [startDate]);
    const endDateNormalized = useMemo(() => normalizeQueryParam(endDate), [endDate]);
    const startDateFormatted = useMemo(() => new Date(startDateNormalized), [startDateNormalized]);
    const endDateFormatted = useMemo(() => new Date(endDateNormalized), [endDateNormalized]);
    const currentDate = useMemo(() => new Date(), []);

    const urlParamsValidation = useMemo(
        () => ({
            missingUrlParams: !startDate || !endDate || !guests,
            validUrlDatesAndGuests: isValidDate(startDateFormatted) && isValidDate(endDateFormatted) && !Number.isNaN(Number(guests)),
            rentalPeriodHasPassed: isBefore(currentDate, endDateFormatted) || isBefore(currentDate, startDateFormatted),
        }),
        [startDate, endDate, guests, startDateFormatted, endDateFormatted, currentDate],
    );

    const hasUrlParamsError =
        urlParamsValidation.missingUrlParams || urlParamsValidation.rentalPeriodHasPassed || !urlParamsValidation.validUrlDatesAndGuests;

    const fieldErrors = useMemo(
        () => ({
            firstname: invalidNameField(firstname, 'Fornavn'),
            surname: invalidNameField(surname, 'Etternavn'),
            dateOfBirth: invalidBirthDate(dateOfBirth) || tooYoung(dateOfBirth) || birthDateTooOld(dateOfBirth),
            phone: invalidPhone(landcode, phone),
            message: invalidMessageField(message),
        }),
        [firstname, surname, dateOfBirth, landcode, phone, message],
    );

    const isFormValid = useMemo(() => !Object.values(fieldErrors).some((error) => Boolean(error)), [fieldErrors]);

    const enableValidation = () => setShouldValidate(true);

    const getFieldError = (field: keyof typeof fieldErrors) => (shouldValidate ? fieldErrors[field] : null);
    const isFieldInvalid = (field: keyof typeof fieldErrors) => shouldValidate && Boolean(fieldErrors[field]);

    return {
        shouldValidate,
        enableValidation,
        isFormValid,
        fieldErrors,
        getFieldError,
        isFieldInvalid,
        urlParamsValidation,
        hasUrlParamsError,
        startDateFormatted,
        endDateFormatted,
    };
};
